import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Typography, Box } from "@mui/material";

const safeMarkdownUrl = (url: string): string => {
  const value = url.trim();
  if ((value.startsWith("/") && !value.startsWith("//")) || value.startsWith("#")) return value;
  try {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:" || protocol === "mailto:" ? value : "";
  } catch {
    return "";
  }
};

/**
 * Renders a chat message as Markdown — Nikola's replies use bold,
 * headers, bullet points, tables, and code-style inline notation
 * (e.g. `Be5`) which all render cleanly via react-markdown + MUI
 * typography. Tables require remark-gfm (GitHub Flavored Markdown).
 */
export const MarkdownMessage = ({ content }: { content: string }) => (
  <Box
    data-chat-prose="current"
    sx={{
      minWidth: 0,
      maxWidth: "100%",
      overflowWrap: "anywhere",
      fontSize: { xs: ".82rem", sm: ".88rem" },
      lineHeight: 1.72,
      "& p": { my: 0, mb: 1.15, fontSize: "inherit", lineHeight: "inherit" },
      "& p:last-child": { mb: 0 },
      "& h1,& h2,& h3": {
        mt: 2,
        mb: 0.85,
        color: "primary.main",
        fontWeight: 950,
        lineHeight: 1.05,
        letterSpacing: "-.045em",
      },
      "& h1:first-of-type,& h2:first-of-type,& h3:first-of-type": { mt: 0 },
      "& ul,& ol": { display: "grid", gap: 0.65, my: 1.1, pl: 0, listStyle: "none" },
      "& li": {
        position: "relative",
        minWidth: 0,
        py: 0.75,
        pr: 0.9,
        pl: 1.5,
        borderInlineStart: "2px solid",
        borderColor: "rgba(255,61,18,.62)",
        bgcolor: "rgba(240,236,226,.025)",
      },
      "& li::before": {
        content: '""',
        position: "absolute",
        insetInlineStart: 0.55,
        top: "1.05em",
        width: 4,
        height: 4,
        bgcolor: "secondary.main",
      },
      "& table": { width: "100%", minWidth: 420, borderCollapse: "collapse", fontSize: "0.75rem" },
      "& th, & td": { border: "1px solid rgba(255,255,255,0.15)", px: 1, py: 0.7, textAlign: "left" },
      "& th": {
        bgcolor: "rgba(240,236,226,0.08)",
        fontWeight: 900,
        color: "primary.main",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      },
      "& code": {
        bgcolor: "rgba(240,236,226,.08)",
        border: "1px solid rgba(240,236,226,.14)",
        px: 0.45,
        py: 0.1,
        color: "primary.main",
        fontWeight: 800,
      },
      "& strong": { color: "primary.main", fontWeight: 950 },
      "& blockquote": {
        m: "1rem 0",
        px: 1.25,
        py: 1,
        borderInlineStart: "3px solid",
        borderColor: "secondary.main",
        bgcolor: "rgba(255,61,18,.06)",
      },
      "& a": { color: "primary.main", textUnderlineOffset: 3, fontWeight: 800 },
    }}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      urlTransform={safeMarkdownUrl}
      components={{
        img: ({ alt }) => <span>{alt}</span>,
        h1: ({ children }) => (
          <Typography component="h1" sx={{ fontSize: "clamp(1.12rem,3vw,1.45rem)" }}>
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography component="h2" sx={{ fontSize: "clamp(1rem,2.5vw,1.25rem)" }}>
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography component="h3" sx={{ fontSize: ".9rem" }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography component="p" sx={{ fontSize: "inherit", lineHeight: "inherit" }}>
            {children}
          </Typography>
        ),
        strong: ({ children }) => <strong>{children}</strong>,
        em: ({ children }) => (
          <Typography component="span" sx={{ color: "text.secondary", fontStyle: "normal", fontSize: ".85em" }}>
            {children}
          </Typography>
        ),
        li: ({ children }) => (
          <li>
            <Typography component="span" sx={{ fontSize: "inherit", lineHeight: "inherit" }}>
              {children}
            </Typography>
          </li>
        ),
        a: ({ children, href }) => (
          <a
            href={href}
            rel="noreferrer noopener"
            target={href?.startsWith("http://") || href?.startsWith("https://") ? "_blank" : undefined}
          >
            {children}
          </a>
        ),
        table: ({ children }) => (
          <Box sx={{ maxWidth: "100%", overflowX: "auto", my: 1.5 }}>
            <Box component="table">{children}</Box>
          </Box>
        ),
        code: ({ children }) => (
          <Box component="code" sx={{ whiteSpace: "break-spaces" }}>
            {children}
          </Box>
        ),
        hr: () => <Box sx={{ borderTop: "2px solid rgba(240,236,226,0.2)", my: 2 }} />,
      }}
    >
      {content}
    </ReactMarkdown>
  </Box>
);
